import { BackendError } from "shared/libraries/backend/BackendError";
import { getToken } from "shared/libraries/auth";
import { ProgressCallback } from "shared/controllers/UserDataManager";

export class BackendModel {
    static base: string = '/norma';
    private static async call<TRequest, TResponse>(method: string, path: string, data?: TRequest) : Promise<TResponse> {
        const token : string = await getToken();
        let command = {
            method: method,
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            }
        };
        if(data) {
            command['body'] = JSON.stringify(data);
        }
        const response = await fetch(path, command);
        if(!response.ok) {
            const text = await response.text();
            throw new BackendError(text);
        }

        // TODO possibly refactor this to a better solution for responses with no content
        if(response.status === 204) {
            return null;
        }

        const json = await response.json();
        return json;
    }
    // TODO implement a list method
    protected static async get<TResponse>(path: string) : Promise<TResponse> {
        return await this.call('GET', path);
    }
    protected static async paginatedGet<TResponse>(path: string, limit: number, offset: number): Promise<TResponse[]> {
        /**
         * TODO: Promote this function as a 1st-citizen functionality.
         *
         * This is a candidate implementation for list method.
         * It has not been promoted yet, because child classes are implementing
         * list functions (such as "find VTs for a project") which conflict
         * with this one.
         */
        async function list(url: string, previousResults = []) {
            return await BackendModel.get<{ results: TResponse[], next: string | undefined }>(url)
                .then(({ results, next }) => {
                    const allResults = [...previousResults, ...results];

                    if ((allResults.length >= limit) || (next === null)) {
                        return allResults;
                    }

                    const index = next.indexOf('/norma');
                    const nextUrl = next.substring(index, next.length);
                    return list(nextUrl, allResults);
                });
        }

        const paginatedUrl = `${path}?limit=${limit}&offset=${offset}`;
        return list(paginatedUrl);
    }
    protected static async post<TRequest, TResponse>(path: string, data?: TRequest) : Promise<TResponse> {
        return await this.call<TRequest, TResponse>('POST', path, data);
    }
    protected static async put<TRequest, TResponse>(path: string, data?: TRequest) : Promise<TResponse> {
        return await this.call<TRequest, TResponse>('PUT', path, data);
    }
    protected static async delete<TResponse>(path: string) : Promise<TResponse> {
        return await this.call('DELETE', path);
    }
    protected static async upload(upload_url: string, content_type: string, file: XMLHttpRequestBodyInit, progress_handler?: ProgressCallback) {
        if(!upload_url) {
            throw new BackendError('Upload URL unavailable');
        }
        if(!content_type) {
            throw new BackendError('Upload Content-Type unavailable');
        }
        const xhr = new XMLHttpRequest();
        return new Promise((resolve, reject) => {
            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                    const progress = event.loaded / event.total;
                    progress_handler?.(progress);
                }
            });
            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    resolve(xhr.response);
                } else {
                    reject({
                        status: xhr.status,
                        statusText: xhr.statusText,
                        response: xhr.response,
                    });
                }
            };
            xhr.onerror = () => {
                reject({
                    status: xhr.status,
                    statusText: xhr.statusText,
                    response: xhr.response,
                });
            };
            xhr.open('PUT', upload_url);
            xhr.setRequestHeader('Content-Type', content_type);
            xhr.setRequestHeader('Cache-Control', 'no-cache');
            xhr.send(file);
        });
    }
}
